/**
 * Règle unique du front pour « l'utilisateur peut-il faire X ? » (ADR-025, ADR-024).
 *
 * Le serveur exige une permission sur chaque écriture ; le front montre une action si et
 * seulement si le serveur l'accepterait. Toute la décision est ici, pure et testée
 * (`write-access.spec.ts`) ; `AuthStore.hasPermission`, `*libHasPermission` et
 * `permissionGuard` passent tous par elle.
 *
 * - PLATFORM_ADMIN : tout.
 * - PLATFORM_SUPPORT : lectures (`*:read`) seulement (ADR-025 Q6).
 * - Org de démo : aucune écriture de données partagées, quel que soit le rôle ou le claim ;
 *   l'ORG_ADMIN garde la gestion de sa propre équipe (`user:admin`, sur `tenant_org_id`).
 * - Sinon : le claim `permissions[]` du jeton fait foi.
 * - Jeton sans claim `permissions` (mapper Keycloak antérieur) : repli sur la table rôle →
 *   permissions du mapper v4, comme le repli F5 des services.
 */

export interface AccessContext {
  roles: readonly string[];
  /** Claim `permissions[]` du jeton ; `null` quand le jeton ne le porte pas du tout. */
  permissions: readonly string[] | null;
  isDemoOrganization: boolean;
}

/**
 * Repli quand le jeton n'a pas de claim `permissions`. Copie des branches ORG_* du mapper v6
 * (`keycloak/mappers/wheere-permissions-mapper.js`) : à tenir identique. v6 (2026-10-07) :
 * `site:request-validation` retirée (circuit de validation de site supprimé). Les rôles plateforme
 * n'y figurent pas, ils sont traités avant.
 */
export const PERMISSIONS_BY_ORG_ROLE: Readonly<Record<string, readonly string[]>> = {
  ORG_ADMIN: [
    'gateway:read',
    'gateway:write',
    'tracker:read',
    'tracker:write',
    'site:read',
    'site:write',
    'site:create',
    'site:delete',
    'site:configure-position',
    'user:read',
    'user:admin',
    'organization:read',
  ],
  ORG_OPERATOR: [
    'gateway:read',
    'gateway:write',
    'tracker:read',
    'tracker:write',
    'site:read',
    'site:write',
    'site:create',
    'site:configure-position',
  ],
  ORG_VIEWER: ['gateway:read', 'tracker:read', 'site:read'],
  ORG_CUSTOMER: ['tracker:read', 'site:read'],
};

/** Ce qu'une org de démo peut obtenir, au mieux (mapper v4, branche `isDemo`). */
const DEMO_ALLOWED = new Set([
  'gateway:read',
  'tracker:read',
  'site:read',
  'user:read',
  'user:admin',
  'organization:read',
]);

const isRead = (permission: string) => permission.endsWith(':read');

/** ADR-024 : l'utilisateur lit une org (`org_id`) qui n'est pas la sienne (`tenant_org_id`). */
export function isDemoOrganization(organizationId: string, tenantOrganizationId: string): boolean {
  return !!tenantOrganizationId && tenantOrganizationId !== organizationId;
}

/**
 * Vrai si l'utilisateur détient au moins une des permissions demandées. Le nom dit l'usage
 * principal (masquer une écriture), mais la règle vaut pour toute permission.
 */
export function canWrite(ctx: AccessContext, required: string | readonly string[]): boolean {
  const list = (typeof required === 'string' ? [required] : required).filter(p => !!p);
  if (list.length === 0) return false;

  if (ctx.roles.includes('PLATFORM_ADMIN')) return true;
  if (ctx.roles.includes('PLATFORM_SUPPORT')) return list.some(isRead);

  const granted = new Set(ctx.permissions ?? ctx.roles.flatMap(r => PERMISSIONS_BY_ORG_ROLE[r] ?? []));
  return list.some(p => granted.has(p) && (!ctx.isDemoOrganization || DEMO_ALLOWED.has(p)));
}
